import { nursingRegistrationFamily } from './nursing-registration-family';
/**
 * Phase 4 §4 — Pre-publish roster gate detection.
 *
 * Pure rule engine. Takes a fully-prepared input (shifts +
 * classifications + screenings + leave + training + terminations + rate
 * versions + period meta) and returns a list of detected gates that
 * must be resolved (BLOCK) or acknowledged (WARN/INFO) before the
 * roster_period can transition DRAFT -> IN_REVIEW -> PUBLISHED.
 *
 * No DB access; the server-side runner in
 * `apps/web/src/lib/roster/gates-runner.ts` loads the data and persists
 * results to `roster_publish_gates`.
 *
 * Rule severity:
 *   BLOCK — publish blocked unless the issue is resolved or a publish-
 *           permitted provider actor records an explicit, audited override.
 *   WARN  — publish allowed; manager must acknowledge.
 *   INFO  — informational; visible in queue without blocking.
 *
 * Initial 13 rules (Phase 4 §4.3 spec). The remaining 3 rules
 * (`WORKER_NOT_AVAILABLE`, `BUDGET_BREACH_ESTIMATE`,
 * `CARE_MINUTES_SHORTFALL`) depend on Components 16/19/§4.8 and ship in
 * a follow-up session.
 */

import type { ComplianceRegulatoryClass } from './compliance-data-state';
import { ROSTER_GATE_REGISTRY, type RosterGateRuleCode } from './roster-gate-registry';
import { instantRangesOverlap, parseInstantMicros } from './instant-micros';
import { compareInstants, compareInstantsDesc } from './roster-chronology';
import { DEFAULT_TZ, dayBoundsInTz } from './timezone';
import { isSleepoverFlankSameShift } from './schads-pr798459';
import {
  evaluateStatutoryScreeningFloor,
  isDirectCareAssignment,
} from './statutory-screening-floor';

export type GateSeverity = 'INFO' | 'WARN' | 'BLOCK';
export type GateScope = 'SHIFT' | 'WORKER_PERIOD' | 'PERIOD' | 'ROSTER_PERIOD';
export type WorkerComplianceEvidenceMode = 'WARN' | 'ENFORCE_ROSTER' | 'ENFORCE_ALL';
export type GateRosterRequirementKind =
  | 'ROLE_TAG'
  | 'CLINICAL_SCOPE_MINIMUM'
  | 'INDUSTRIAL_CLASSIFICATION_MINIMUM'
  | 'AGENCY_SUPPLIER'
  | 'SALARY_CONTEXT';

export interface RosterGate {
  /**
   * Registry-typed. A code that is not in ROSTER_GATE_REGISTRY is a compile
   * error, which is the only reliable way to keep the registry complete —
   * source-scanning for rule codes was attempted and defeated four separate
   * times by literals, constants, ternaries and cross-file raises, silently
   * omitting two of the eight non-overridable safety codes.
   */
  ruleCode: RosterGateRuleCode;
  severity: GateSeverity;
  scope: GateScope;
  scopeRefType: string; // 'shift', 'worker_period', 'period'
  scopeRef: string;
  message: string;
  suggestedAction?: string;
  publicationConsequence?: 'PAY_HOLD' | 'CLINICAL_BLOCK';
  fixHref?: string;
  regulatory_class?: ComplianceRegulatoryClass;
}

export interface GateShift {
  id: string;
  /** Optional persisted-source reference for gate rows. Defaults to shift/id. */
  scopeRefType?: 'shift' | 'slot' | 'assignment';
  scopeRef?: string;
  staffId: string | null; // null = unassigned (only some gates apply)
  date: string; // YYYY-MM-DD
  startTime: string; // ISO datetime
  endTime: string; // ISO datetime
  /** Tenant-local minute-of-day for availability windows. End may exceed 1440 for overnight shifts. */
  localStartMinutes?: number;
  localEndMinutes?: number;
  breakMinutes: number;
  serviceType: string | null;
  /**
   * Controlled service code when present (SAH inclusion / shift.service_code).
   * Feeds the statutory screening floor the same way SQL uses p_service_code.
   */
  serviceCode?: string | null;
  /**
   * Assigned worker's staff.role. Required for the statutory floor when the
   * service line is vague (`07.00 - 15.00`) and required_role is empty — the
   * Medea pattern — because the worker role is then the only direct-care signal.
   * Mirrors SQL `v_staff_role` in roster_v2_slot_assignment_hard_blockers.
   */
  staffRole?: string | null;
  facilityId: string | null;
  /**
   * Home care vs residential, resolved from the SHIFT's own shape rather than
   * the tenant's org type: a client-scoped shift sited in no wing is home care.
   * Mirrors resolveGateServiceModel in gates-runner and the same discriminator
   * used by roster_v2_slot_assignment_hard_blockers, so the app gate and the DB
   * predicate cannot disagree about what a home-care visit is.
   *
   * Needed because MA000100 cl 25.6 applies only to home care (and SACS
   * disability services) employees — the rest-break rule below reads it.
   * Undefined means "not established"; every rule that consults it must fail
   * towards the residential reading, which is the stricter one.
   */
  serviceContext?: 'HOME_CARE' | 'RESIDENTIAL' | null;
  /**
   * True when this period of work immediately precedes or follows a
   * SCHADS cl 25.7 sleepover. Both flanks must be marked for PR798459
   * cl 25.4(c) to skip TEN_HOUR_BREAK between them.
   */
  hasSleepover?: boolean;
  /** SCHADS cl 25.10 remote work — does not count for TEN_HOUR_BREAK (cl 25.10(f)(iii)(B)). */
  isRemoteWork?: boolean;
  /** Required SCHADS classification for this shift (e.g. 'L3', 'L5', 'RN'). */
  requiredClassification?: string | null;
  /** Operational roster label, e.g. PCA, Catering, Lead RN. */
  requiredRole?: string | null;
  /**
   * Tenant pay-role context for this event, resolved from the roster role
   * mapping or existing assignment pay context. Normal alternate-role work
   * should use this rather than comparing against the worker's substantive
   * classification.
   */
  tenantPayRoleId?: string | null;
  staffPayRoleAssignmentId?: string | null;
  rosterRequirementKind?: GateRosterRequirementKind | null;
  minimumScopeRole?: string | null;
  payContextStatus?: string | null;
  payContextSnapshot?: Record<string, unknown> | null;
  plannedWorkingUpSnapshot?: Record<string, unknown> | null;
  requiresPayFactCapture?: boolean;
  /** Required screenings (e.g. ['POLICE','FIRST_AID']). */
  requiredScreenings?: string[];
  /** Required training codes (e.g. ['MEDICATION_ENDORSEMENT']). */
  requiredTraining?: string[];
  /** Permitted by an explicit allowance to exceed daily/weekly limits (rare). */
  excessHoursAllowance?: boolean;
}

export interface GateClassification {
  staffId: string;
  awardCode: string | null;
  classificationStream: string | null;
  classificationLevel: string | null;
  employmentType: string; // FULL_TIME, PART_TIME, CASUAL, CONTRACTOR
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface GateScreening {
  staffId: string;
  screeningType: string;
  expiresAt: string | null; // YYYY-MM-DD
  status: string; // CLEARED, EXPIRED, REJECTED, etc.
  ahpraVerificationStatus?: string | null;
}

export interface GateLeaveSegment {
  segmentDate: string; // YYYY-MM-DD
  startAt: string | null;
  endAt: string | null;
  units: number;
}

export interface GateLeaveRequest {
  staffId: string;
  startDate: string; // YYYY-MM-DD
  endDate: string;
  status: string; // APPROVED counts; PENDING/DECLINED ignored
  /**
   * RCA-1.2 — ABSENCE and PAYABILITY are different questions (owner decision
   * D5). `status` answers "is this worker away". This answers "is that absence
   * payable", and it only moves the SEVERITY.
   *
   * Undefined means unknown, which is treated as payable so the rule keeps its
   * historic BLOCK. `false` means the worker is still away — the leave was
   * approved — but the pay plane has not resolved it, so the coordinator gets a
   * warning rather than a wedged assignment.
   */
  payable?: boolean;
  /**
   * Units-bearing leave_request_segments. Timed windows overlap the shift
   * tstzrange; full-day (null timestamps) overlap the tenant-local day.
   * When omitted or empty, the approved calendar span is the fallback.
   */
  segments?: GateLeaveSegment[];
}

export interface GateTrainingRecord {
  staffId: string;
  trainingCode: string;
  status: string; // COMPLETED, EXPIRED, IN_PROGRESS, NOT_STARTED
  completedAt: string | null;
  expiresAt: string | null;
  /** Assignment id. Required for a deterministic tie-break between occurrences. */
  assignmentId?: string | null;
  /**
   * True when a renewal occurrence supersedes this row.
   *
   * A lapsed completion keeps its `expiresAt` — currency gates depend on that date
   * — so it stays in this list as history. It must not be the record the gate
   * judges the worker on once a renewal exists, or the gate would keep reporting
   * the old expiry after the renewal is completed.
   */
  isSuperseded?: boolean | null;
}

export interface GateWorkRightsEvidence {
  staffId: string;
  status: string;
  expiresAt: string | null; // YYYY-MM-DD; null = non-expiring or no expiry captured.
  updatedAt?: string | null;
}

export interface GateStaffTermination {
  staffId: string;
  terminatedAt: string | null; // YYYY-MM-DD; null = active
}

export interface GateRateVersion {
  awardCode: string;
  effectiveFrom: string;
  effectiveTo: string | null; // null = currently active
}

export interface GateWorkerPayProfile {
  staffId: string;
  paymentMethod?: string | null;
  annualisedSalary?: number | null;
  salaryPayFrequency?: string | null;
  confirmedSalariedOrCommonLaw?: boolean;
  excludeFromHourlyAwardExport?: boolean;
}

export interface GateTenantPayRole {
  id: string;
  code: string;
  title?: string | null;
  status: string;
  roleKind?: string | null;
  baseAwardCode?: string | null;
  classificationStream?: string | null;
  classificationLevel?: string | null;
  rosterRequirementKind?: GateRosterRequirementKind | null;
  minimumScopeRole?: string | null;
  screeningRequirements?: string[];
  trainingRequirements?: string[];
}

export interface GateStaffPayRoleAssignment {
  id: string;
  staffId: string;
  tenantPayRoleId: string;
  status: string;
  eligibilityStatus: string;
  effectiveFrom: string;
  /** Exclusive upper bound: assignment coverage is [effectiveFrom, effectiveTo). */
  effectiveTo: string | null;
}

/**
 * Phase 4 Component 16 — worker availability declaration.
 * The gate `WORKER_NOT_AVAILABLE` fires when a shift conflicts with an
 * UNAVAILABLE declaration on the assigned worker. AVAILABLE / PREFERRED
 * declarations are informational only (no gate).
 */
export interface GateWorkerAvailability {
  id: string;
  staffId: string;
  availabilityType: 'AVAILABLE' | 'UNAVAILABLE' | 'PREFERRED';
  pattern:
    | 'ONE_TIME'
    | 'RECURRING_WEEKLY'
    | 'RECURRING_FORTNIGHTLY'
    | 'RECURRING_MONTHLY'
    | 'RRULE';
  dayOfWeek?: number | null;
  startTime?: string | null;
  endTime?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  recurrenceRule?: string | null;
  status: 'ACTIVE' | 'EXPIRED' | 'REVOKED';
}

/**
 * One-off availability exception (`worker_availability_exceptions`).
 *
 * Distinct from `GateWorkerAvailability` on purpose: an exception is an
 * absolute instant range, never a recurrence or a tenant-local date window.
 * Squeezing it into the pattern shape loses the timezone-correct boundaries,
 * so it carries its own type and its own matcher.
 */
export interface GateWorkerAvailabilityException {
  id: string;
  staffId: string;
  availabilityType: 'AVAILABLE' | 'UNAVAILABLE' | 'PREFERRED';
  /** Absolute ISO timestamp (timestamptz). */
  startsAt: string;
  /** Absolute ISO timestamp (timestamptz). */
  endsAt: string;
  status: 'ACTIVE' | 'EXPIRED' | 'REVOKED';
}

export interface GatePeriodMeta {
  rosterPeriodId: string;
  periodStart: string; // YYYY-MM-DD
  periodEnd: string;
  /** Notional publish date used to evaluate LATE_PUBLISH_NOTICE. Defaults to today. */
  evaluatedAt?: string; // ISO date
}

export interface GateInput {
  /** Default-OFF provider assessment. This adds a review warning, never a care refusal. */
  clinicalRegistrationAssessmentEnabled?: boolean;
  clinicalRegistrationAssessments?: Array<{
    staffId: string;
    date: string;
    currentRn: boolean;
    currentEn: boolean;
  }>;
  /** Tenant-only D21 opt-in; omitted/false preserves legacy output exactly. */
  publishUnblockEnabled?: boolean;
  shifts: GateShift[];
  classifications: GateClassification[];
  screenings: GateScreening[];
  leave: GateLeaveRequest[];
  training: GateTrainingRecord[];
  workRights?: GateWorkRightsEvidence[];
  terminations: GateStaffTermination[];
  rateVersions: GateRateVersion[];
  periodMeta: GatePeriodMeta;
  /** Optional — Component 16. When omitted, WORKER_NOT_AVAILABLE gate is skipped. */
  availability?: GateWorkerAvailability[];
  /**
   * Optional — one-off `worker_availability_exceptions`. Supplied separately
   * from `availability` because these are absolute instant ranges. When
   * omitted, exception-sourced unavailability is skipped by the gate (the
   * assignment RPCs still hard-block on it via
   * `shift_feasibility_mv.availability_exception_ok`).
   */
  availabilityExceptions?: GateWorkerAvailabilityException[];
  /** Optional — Component 19. When set, BUDGET_BREACH_ESTIMATE evaluates total
   *  estimated wages against this budget. */
  estimatedTotalWages?: number;
  periodBudget?: number;
  /** Optional — §4.8. Per-day care-minutes shortfall (residential only).
   *  Caller supplies the aggregated breach count from calculateCareMinutesAchievement. */
  careMinutesBreachDays?: number;
  careMinutesPeriodTotalShortfall?: number;
  careMinutesPeriodRnShortfall?: number;
  /**
   * Optional worker pay-profile hints. When a worker is explicitly salary,
   * annualised, or common-law/non-hourly, missing hourly EA classification
   * evidence is still surfaced but should not block roster publication as an
   * hourly award setup failure.
   */
  workerPayProfiles?: GateWorkerPayProfile[];
  /** Optional assignment-level pay role context for multi-role/EA tenants. */
  tenantPayRoles?: GateTenantPayRole[];
  staffPayRoleAssignments?: GateStaffPayRoleAssignment[];
  /**
   * Tenant-level worker compliance enforcement. When omitted, the engine keeps
   * the historical fail-closed behaviour. WARN downgrades missing/not-yet-
   * captured worker compliance evidence while keeping known-bad/expired
   * evidence as BLOCK.
   */
  workerComplianceEvidenceMode?: WorkerComplianceEvidenceMode;
  /** Tenant IANA timezone for full-day leave windows. Defaults to Australia/Sydney. */
  timezone?: string;
}

const SCREENING_TYPES_REQUIRED = new Set([
  'NATIONAL_POLICE_CHECK',
  'NDIS_WORKER_SCREENING',
  'AGED_CARE_WORKER_SCREENING',
  'WORKING_WITH_CHILDREN',
  'AHPRA_REGISTRATION',
  'STATUTORY_DECLARATION',
  'INTERNATIONAL_POLICE_CHECK',
  'FIRST_AID',
]);

/** Readable screening names for gate messages; never show the stored code. */
function screeningLabel(type: string): string {
  const labels: Record<string, string> = {
    NATIONAL_POLICE_CHECK: 'national police check',
    NDIS_WORKER_SCREENING: 'NDIS worker screening',
    AGED_CARE_WORKER_SCREENING: 'aged care worker screening',
    WORKER_SCREENING_CLEARANCE: 'worker screening clearance',
    WORKING_WITH_CHILDREN: 'working with children check',
    AHPRA_REGISTRATION: 'AHPRA registration',
    STATUTORY_DECLARATION: 'statutory declaration',
    INTERNATIONAL_POLICE_CHECK: 'international police check',
    FIRST_AID: 'first aid certification',
  };
  return labels[type] ?? type.replace(/_/g, ' ').toLowerCase();
}

const SCREENING_KNOWN_BAD_STATUSES = new Set([
  'EXPIRED',
  'REVOKED',
  'FAILED',
  'REJECTED',
  'EXCLUDED',
  'REQUIRES_REVIEW',
]);

function localDayWindowMicros(
  ymd: string,
  timezone: string,
): { start: number; end: number } | null {
  try {
    const bounds = dayBoundsInTz(ymd, timezone);
    const start = parseInstantMicros(bounds.startUtc);
    const endInclusive = parseInstantMicros(bounds.endUtc);
    if (start == null || endInclusive == null) return null;
    return { start, end: endInclusive + 1 };
  } catch {
    return null;
  }
}

function approvedLeaveCoversShift(
  leave: GateLeaveRequest,
  shift: GateShift,
  timezone: string,
): boolean {
  if (leave.status !== 'APPROVED' || leave.staffId !== shift.staffId) return false;
  const shiftStart = parseInstantMicros(shift.startTime);
  const shiftEnd = parseInstantMicros(shift.endTime);
  const segments = (leave.segments ?? []).filter((segment) => segment.units > 0);
  if (shiftStart == null || shiftEnd == null) {
    return leave.startDate <= shift.date && leave.endDate >= shift.date;
  }
  if (segments.length === 0) {
    const start = localDayWindowMicros(leave.startDate, timezone);
    const end = localDayWindowMicros(leave.endDate, timezone);
    if (!start || !end) {
      return leave.startDate <= shift.date && leave.endDate >= shift.date;
    }
    return instantRangesOverlap(shiftStart, shiftEnd, start.start, end.end);
  }
  const segmentCovers = segments.some((segment) => {
    if (segment.startAt && segment.endAt) {
      const start = parseInstantMicros(segment.startAt);
      const end = parseInstantMicros(segment.endAt);
      if (start == null || end == null) return false;
      return instantRangesOverlap(shiftStart, shiftEnd, start, end);
    }
    const day = localDayWindowMicros(segment.segmentDate, timezone);
    if (!day) return segment.segmentDate === shift.date;
    return instantRangesOverlap(shiftStart, shiftEnd, day.start, day.end);
  });
  if (segmentCovers) return true;
  const hasSegmentOnShiftDate = segments.some((segment) => segment.segmentDate === shift.date);
  if (!hasSegmentOnShiftDate && leave.startDate <= shift.date && leave.endDate >= shift.date) {
    return true;
  }
  return false;
}

const TRAINING_KNOWN_BAD_STATUSES = new Set([
  'EXPIRED',
  'REVOKED',
  'FAILED',
  'REJECTED',
  'OVERDUE',
]);
const WORK_RIGHTS_CURRENT_NON_EXPIRING_STATUSES = new Set([
  'SIGNED',
  'COUNTERSIGNED',
  'VERIFIED',
  'IMPORTED',
]);
const WORK_RIGHTS_KNOWN_BAD_STATUSES = new Set([
  'EXPIRED',
  'REVOKED',
  'FAILED',
  'REJECTED',
  'CANCELLED',
  'ABANDONED',
]);
const AHPRA_KNOWN_BAD_VERIFICATION_STATUSES = new Set(['LAPSED', 'NOT_FOUND']);

const RN_REQUIREMENTS = new Set([
  'RN',
  'RNAGY',
  'RN_AGENCY',
  'REGISTERED_NURSE',
  'REGISTERED_NURSING',
  'NURSE_PRACTITIONER',
  'NP',
]);
const LEAD_RN_REQUIREMENTS = new Set(['LRN', 'LEAD_RN', 'LEAD_REGISTERED_NURSE']);
const EN_REQUIREMENTS = new Set(['EN', 'ENROLLED_NURSE', 'ENROLLED_NURSING']);
const HCA_RANK = new Map<string, number>([
  ['L1', 1],
  ['L2', 2],
  ['L3', 3],
  ['L4', 4],
  ['L5', 5],
  ['L6', 6],
  ['L7', 7],
  ['L8', 8],
  ['HCA1', 1],
  ['HCA2', 2],
  ['HCA3', 3],
  ['HCA4', 4],
  ['HCA5', 5],
  ['HCA6', 6],
  ['CARE_WORKER', 1],
  ['PERSONAL_CARE_WORKER', 1],
  ['PCW', 1],
  ['AIN', 1],
  ['ASSISTANT_IN_NURSING', 1],
]);
const DIRECT_CARE_REQUIREMENTS = new Set([
  'PCA',
  'PCW',
  'HCA',
  'AIN',
  'CARE_WORKER',
  'PERSONAL_CARE',
  'PERSONAL_CARE_WORKER',
  'ASSISTANT_IN_NURSING',
]);
const LIFESTYLE_REQUIREMENTS = new Set([
  'LAO',
  'LIFESTYLE',
  'LIFESTYLE_ACTIVITY',
  'LIFESTYLE_ACTIVITY_OFFICER',
  'LIFESTYLE_OFFICER',
]);
const SERVICE_REQUIREMENTS = new Set([
  'SRV',
  'SERVICE',
  'SERVICES',
  'CTR',
  'CATERING',
  'COOK',
  'CHEF',
  'LND',
  'LAUNDRY',
  'CLN',
  'CLEANING',
  'MNT',
  'MAINTENANCE',
  'HOTEL_SERVICES',
]);
const ADMIN_REQUIREMENTS = new Set([
  'ADM',
  'ADMIN',
  'ADMINISTRATION',
  'FIN',
  'FINANCE',
  'HR',
  'CLO',
  'CLIENT_LIAISON',
  'CLIENT_LIASION',
  'CSC',
  'QEO',
  'COO',
  'CRQ',
  'CM',
  'CARE_MANAGEMENT',
]);

function dayDiff(a: string, b: string): number {
  // Whole-day difference between YYYY-MM-DD strings (b - a).
  return Math.round(
    (new Date(b + 'T00:00:00Z').getTime() - new Date(a + 'T00:00:00Z').getTime()) /
      (1000 * 60 * 60 * 24),
  );
}

function hoursBetween(start: string, end: string): number {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.max(0, ms / (1000 * 60 * 60));
}

function shiftPaidHours(s: GateShift): number {
  return Math.max(0, hoursBetween(s.startTime, s.endTime) - s.breakMinutes / 60);
}

function gateScopeRefType(s: GateShift): string {
  return s.scopeRefType ?? 'shift';
}

function gateScopeRef(s: GateShift): string {
  return s.scopeRef ?? s.id;
}

function dateInRange(date: string, from: string, to: string | null): boolean {
  if (date < from) return false;
  if (to !== null && date > to) return false;
  return true;
}

function classificationCovers(c: GateClassification, date: string): boolean {
  return dateInRange(date, c.effectiveFrom, c.effectiveTo);
}

function rateCovers(r: GateRateVersion, date: string): boolean {
  return dateInRange(date, r.effectiveFrom, r.effectiveTo);
}

function payRoleAssignmentCovers(a: GateStaffPayRoleAssignment, date: string): boolean {
  if (date < a.effectiveFrom) return false;
  if (a.effectiveTo !== null && date >= a.effectiveTo) return false;
  return true;
}

function normaliseRosterRequirementKind(
  value: string | null | undefined,
): GateRosterRequirementKind | null {
  const normalised = normaliseClassificationCode(value ?? null);
  if (
    normalised === 'ROLE_TAG' ||
    normalised === 'CLINICAL_SCOPE_MINIMUM' ||
    normalised === 'INDUSTRIAL_CLASSIFICATION_MINIMUM' ||
    normalised === 'AGENCY_SUPPLIER' ||
    normalised === 'SALARY_CONTEXT'
  ) {
    return normalised;
  }
  return null;
}

function activeEligiblePayRoleAssignment(
  assignments: GateStaffPayRoleAssignment[],
  shift: GateShift,
  tenantPayRoleId: string,
): GateStaffPayRoleAssignment | null {
  if (!shift.staffId) return null;
  if (shift.staffPayRoleAssignmentId) {
    const explicit = assignments.find((a) => a.id === shift.staffPayRoleAssignmentId);
    if (!explicit) return null;
    return explicit.staffId === shift.staffId &&
      explicit.tenantPayRoleId === tenantPayRoleId &&
      normaliseStatus(explicit.status) === 'ACTIVE' &&
      normaliseStatus(explicit.eligibilityStatus) === 'ELIGIBLE' &&
      payRoleAssignmentCovers(explicit, shift.date)
      ? explicit
      : null;
  }
  return (
    assignments.find(
      (a) =>
        a.staffId === shift.staffId &&
        a.tenantPayRoleId === tenantPayRoleId &&
        normaliseStatus(a.status) === 'ACTIVE' &&
        normaliseStatus(a.eligibilityStatus) === 'ELIGIBLE' &&
        payRoleAssignmentCovers(a, shift.date),
    ) ?? null
  );
}

function uniqueCodes(...groups: Array<string[] | undefined>): string[] {
  const out = new Set<string>();
  for (const group of groups) {
    for (const value of group ?? []) {
      const trimmed = value.trim();
      if (trimmed) out.add(trimmed);
    }
  }
  return [...out];
}

function payRoleLabelCoversRequirement(
  role: GateTenantPayRole | undefined,
  requirement: string | null | undefined,
): boolean {
  if (!role || !requirement) return false;
  const required = normaliseClassificationCode(requirement);
  if (!required) return false;
  const roleTokens = classificationTokens(
    role.code,
    role.title,
    role.classificationStream,
    role.classificationLevel,
  );
  return roleTokens.has(required);
}

function isNonHourlyPayProfile(profile: GateWorkerPayProfile | undefined): boolean {
  if (!profile) return false;
  if (profile.confirmedSalariedOrCommonLaw || profile.excludeFromHourlyAwardExport) return true;
  if (profile.annualisedSalary != null && profile.annualisedSalary > 0) return true;
  const method = normaliseClassificationCode(profile.paymentMethod ?? null);
  return (
    method === 'SALARY' ||
    method === 'SALARY_FLAT' ||
    method === 'ANNUALISED_SALARY' ||
    method === 'ANNUALIZED_SALARY' ||
    method === 'COMMON_LAW' ||
    method === 'NON_HOURLY'
  );
}

function hasResolvedSalaryFlatContext(shift: GateShift): boolean {
  const snapshot = shift.payContextSnapshot;
  return Boolean(
    normaliseStatus(shift.payContextStatus ?? '') === 'RESOLVED' &&
    snapshot?.schemaVersion === 2 &&
    snapshot.baseAwardCode === 'SALARY_FLAT' &&
    snapshot.staffId === shift.staffId &&
    snapshot.eventDate === shift.date &&
    snapshot.sourceKind === 'ROSTER_ASSIGNMENT',
  );
}

function normaliseTrainingCode(value: string): string {
  return value.trim().toUpperCase();
}

function normaliseStatus(value: string): string {
  return value.trim().toUpperCase();
}

function currentWorkRightsForWorker(
  records: GateWorkRightsEvidence[] | undefined,
  staffId: string,
): GateWorkRightsEvidence | null {
  const workerRecords = (records ?? []).filter((record) => record.staffId === staffId);
  if (workerRecords.length === 0) return null;

  const nonExpiring = workerRecords
    .filter((record) => {
      if (record.expiresAt) return false;
      const status = normaliseStatus(record.status);
      return (
        WORK_RIGHTS_CURRENT_NON_EXPIRING_STATUSES.has(status) ||
        WORK_RIGHTS_KNOWN_BAD_STATUSES.has(status)
      );
    })
    .sort((a, b) => {
      // `updatedAt` is a timestamptz, so order by instant. This sort picks WHICH
      // work-rights record the BLOCK gate then judges the worker on, so getting
      // the most-recent wrong can gate on a superseded status. (`expiresAt`
      // below stays on localeCompare — it is a bare YYYY-MM-DD date, where
      // lexicographic ordering is exactly right.)
      const timestampOrder = compareInstantsDesc(a.updatedAt, b.updatedAt);
      if (timestampOrder !== 0) return timestampOrder;
      return (
        Number(WORK_RIGHTS_KNOWN_BAD_STATUSES.has(normaliseStatus(b.status))) -
        Number(WORK_RIGHTS_KNOWN_BAD_STATUSES.has(normaliseStatus(a.status)))
      );
    })[0];
  if (nonExpiring) return nonExpiring;

  const expiring = workerRecords
    .filter((record) => record.expiresAt)
    .sort((a, b) => {
      // expiresAt is a bare YYYY-MM-DD date — lexicographic is correct and stays.
      // updatedAt is a timestamptz, so its tiebreak compares instants.
      const expiryOrder = String(b.expiresAt).localeCompare(String(a.expiresAt));
      if (expiryOrder !== 0) return expiryOrder;
      return compareInstantsDesc(a.updatedAt, b.updatedAt);
    });
  if (expiring[0]) return expiring[0];

  return (
    workerRecords.find((record) =>
      WORK_RIGHTS_KNOWN_BAD_STATUSES.has(normaliseStatus(record.status)),
    ) ?? null
  );
}

/**
 * The record a shift's training requirement should be judged on.
 *
 * This used to be `input.training.find(...)` — first match, over a query with no
 * ORDER BY. With one assignment per course that was stable by accident. Renewal
 * occurrences make it two rows (a lapsed completion and its successor), and the
 * gate would then flip between EXPIRED_REQUIRED_TRAINING and
 * MISSING_REQUIRED_TRAINING on whatever order the database happened to return —
 * and could keep blocking on the expired predecessor after the renewal was done.
 *
 * Rules, in order:
 *   1. superseded rows are history, never the subject of the gate;
 *   2. any record that is currently valid clears the requirement;
 *   3. otherwise report the strongest evidence the worker has — latest expiry —
 *      so the message names the most recent lapse, not an arbitrary one.
 */
export function selectTrainingRecordForRequirement(
  records: readonly GateTrainingRecord[],
  staffId: string,
  requiredCode: string,
  onDate: string,
): GateTrainingRecord | null {
  const matches = records.filter(
    (t) => t.staffId === staffId && normaliseTrainingCode(t.trainingCode) === requiredCode,
  );
  if (matches.length === 0) return null;

  const live = matches.filter((t) => t.isSuperseded !== true);
  // If every match is superseded the lineage is inconsistent; judge on all of them
  // rather than returning nothing, because "no record" reads as never-trained.
  const pool = live.length > 0 ? live : matches;

  const current = pool.filter(
    (t) =>
      normaliseStatus(t.status) === 'COMPLETED' &&
      !TRAINING_KNOWN_BAD_STATUSES.has(normaliseStatus(t.status)) &&
      (t.expiresAt === null || t.expiresAt >= onDate),
  );
  const candidates = current.length > 0 ? current : pool;

  return (
    [...candidates].sort((a, b) => {
      const ax = a.expiresAt ?? '';
      const bx = b.expiresAt ?? '';
      if (ax !== bx) return ax < bx ? 1 : -1; // latest expiry first
      return String(a.assignmentId ?? '').localeCompare(String(b.assignmentId ?? ''));
    })[0] ?? null
  );
}

function normaliseClassificationCode(value: string | null): string {
  return (value ?? '')
    .trim()
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function classificationTokens(...values: Array<string | null | undefined>): Set<string> {
  const tokens = new Set<string>();
  for (const value of values) {
    const normalised = normaliseClassificationCode(value ?? null);
    if (!normalised) continue;
    tokens.add(normalised);
    for (const part of normalised.split('_')) {
      if (part) tokens.add(part);
    }
  }
  return tokens;
}

function hasAnyToken(tokens: Set<string>, expected: Set<string>): boolean {
  for (const token of tokens) {
    if (expected.has(token)) return true;
  }
  return false;
}

/**
 * Match a classification FAMILY code within a worker's tokens.
 *
 * RCA-2.7. This used to accept only `HCA`, `HCA_...` and `..._HCA_...`, which
 * meant the stored level `HCA3` did not match the family `HCA` — despite `HCA`
 * already being in DIRECT_CARE_REQUIREMENTS and HCA_RANK already mapping
 * HCA1..HCA8. The intent was never in doubt; the helper simply did not
 * implement it, and its name said "prefix" while its body required an
 * underscore boundary.
 *
 * Measured consequence on production 2026-08-25: Golden Bloom Care carried 108
 * OPEN BLOCK gates reading "Shift requires CARE_WORKER; worker is
 * home_care_aged/HCA3" — its entire published roster — against workers whose
 * classification is CORRECT for a home-care provider. Not a data problem: the
 * rule was asking the wrong question of the right answer.
 *
 * `${prefix}<digits>` is added deliberately rather than a bare `startsWith`,
 * which would let `HCA` swallow an unrelated `HCARE`-style token. Every caller
 * passes a family code (RN, EN, PCA, PCW, HCA, AIN, GS_PCA, LIFESTYLE, SRV,
 * ADM …), and a family code followed by digits is a level within that family.
 */
function hasTokenPrefix(tokens: Set<string>, prefix: string): boolean {
  for (const token of tokens) {
    if (token === prefix || token.startsWith(`${prefix}_`) || token.includes(`_${prefix}_`)) {
      return true;
    }
    // Level within the family: HCA3, PCA2, RN1, EN4, GS_PCA_L2 handled above.
    if (token.length > prefix.length && token.startsWith(prefix)) {
      const suffix = token.slice(prefix.length);
      if (/^\d+$/.test(suffix)) return true;
    }
  }
  return false;
}

function isRnClassification(
  stream: string | null,
  level: string | null,
  tokens = classificationTokens(stream, level),
): boolean {
  const levelCode = normaliseClassificationCode(level);
  const streamCode = normaliseClassificationCode(stream);
  return (
    RN_REQUIREMENTS.has(streamCode) ||
    RN_REQUIREMENTS.has(levelCode) ||
    hasAnyToken(tokens, RN_REQUIREMENTS) ||
    hasTokenPrefix(tokens, 'RN') ||
    hasTokenPrefix(tokens, 'REGISTERED_NURSE') ||
    hasTokenPrefix(tokens, 'NURSE_PRACTITIONER')
  );
}

function isLeadRnClassification(
  stream: string | null,
  level: string | null,
  tokens = classificationTokens(stream, level),
): boolean {
  const levelCode = normaliseClassificationCode(level);
  const streamCode = normaliseClassificationCode(stream);
  return (
    LEAD_RN_REQUIREMENTS.has(streamCode) ||
    LEAD_RN_REQUIREMENTS.has(levelCode) ||
    hasAnyToken(tokens, LEAD_RN_REQUIREMENTS) ||
    levelCode.startsWith('LEAD_RN') ||
    levelCode.startsWith('LEAD_REGISTERED_NURSE')
  );
}

function isEnClassification(
  stream: string | null,
  level: string | null,
  tokens = classificationTokens(stream, level),
): boolean {
  const levelCode = normaliseClassificationCode(level);
  const streamCode = normaliseClassificationCode(stream);
  return (
    EN_REQUIREMENTS.has(streamCode) ||
    EN_REQUIREMENTS.has(levelCode) ||
    hasAnyToken(tokens, EN_REQUIREMENTS) ||
    hasTokenPrefix(tokens, 'EN') ||
    hasTokenPrefix(tokens, 'ENROLLED_NURSE')
  );
}

function isDirectCareClassification(tokens: Set<string>): boolean {
  return (
    hasAnyToken(tokens, DIRECT_CARE_REQUIREMENTS) ||
    hasTokenPrefix(tokens, 'PCA') ||
    hasTokenPrefix(tokens, 'PCW') ||
    hasTokenPrefix(tokens, 'HCA') ||
    hasTokenPrefix(tokens, 'AIN') ||
    hasTokenPrefix(tokens, 'GS_PCA')
  );
}

function isLifestyleClassification(tokens: Set<string>): boolean {
  return hasAnyToken(tokens, LIFESTYLE_REQUIREMENTS) || hasTokenPrefix(tokens, 'LIFESTYLE');
}

function isGeneralStaffServiceClassification(tokens: Set<string>): boolean {
  return (
    hasAnyToken(tokens, SERVICE_REQUIREMENTS) ||
    hasTokenPrefix(tokens, 'GS_SRV') ||
    hasTokenPrefix(tokens, 'SRV')
  );
}

function isGeneralStaffAdminClassification(tokens: Set<string>): boolean {
  return (
    hasAnyToken(tokens, ADMIN_REQUIREMENTS) ||
    hasTokenPrefix(tokens, 'GS_ADM') ||
    hasTokenPrefix(tokens, 'ADM')
  );
}

/**
 * Phase 4 Component 16 — match a shift against UNAVAILABLE windows. Returns
 * the first matching window or null. Mirrors the logic in
 * `worker-availability.ts::resolveWorkerAvailability` but inlined here to
 * avoid cross-module dependency cycles in the roster-gate engine.
 */
function isShiftCoveredByUnavailableWindow(
  windows: GateWorkerAvailability[],
  shift: GateShift,
): GateWorkerAvailability | null {
  if (!shift.staffId) return null;
  for (const w of windows) {
    if (w.staffId !== shift.staffId) continue;
    if (w.status !== 'ACTIVE') continue;
    if (w.availabilityType !== 'UNAVAILABLE') continue;
    // Time-of-day overlap (if window has times)
    if (w.startTime || w.endTime) {
      const winStart = w.startTime
        ? parseInt(w.startTime.split(':')[0]!, 10) * 60 + parseInt(w.startTime.split(':')[1]!, 10)
        : 0;
      let winEnd = w.endTime
        ? parseInt(w.endTime.split(':')[0]!, 10) * 60 + parseInt(w.endTime.split(':')[1]!, 10)
        : 24 * 60;
      const shiftStartMs = new Date(shift.startTime).getTime();
      const shiftEndMs = new Date(shift.endTime).getTime();
      const dayMs = new Date(shift.date + 'T00:00:00Z').getTime();
      const shiftStartMin = shift.localStartMinutes ?? Math.floor((shiftStartMs - dayMs) / 60000);
      let shiftEndMin = shift.localEndMinutes ?? Math.floor((shiftEndMs - dayMs) / 60000);
      if (shiftEndMin < shiftStartMin) shiftEndMin += 24 * 60;
      const overlapRanges: Array<[number, number]> = [];
      if (winEnd <= winStart) {
        winEnd += 24 * 60;
        if (availabilityWindowMatchesDate(w, shift.date)) {
          overlapRanges.push([winStart, winEnd]);
        }
        const previousDate = previousDateString(shift.date);
        if (availabilityWindowMatchesDate(w, previousDate)) {
          overlapRanges.push([winStart - 24 * 60, winEnd - 24 * 60]);
        }
      } else if (availabilityWindowMatchesDate(w, shift.date)) {
        overlapRanges.push([winStart, winEnd]);
      }
      if (
        overlapRanges.length === 0 ||
        overlapRanges.every(
          ([rangeStart, rangeEnd]) => shiftStartMin >= rangeEnd || shiftEndMin <= rangeStart,
        )
      ) {
        continue;
      }
    } else if (!availabilityWindowMatchesDate(w, shift.date)) {
      continue;
    }
    return w;
  }
  return null;
}

/**
 * One-off UNAVAILABLE exception covering a shift.
 *
 * Both sides are absolute instants, so this is a plain half-open interval
 * overlap and needs no timezone or recurrence reasoning. `[start, end)` mirrors
 * the `tstzrange(..., '[)') && tstzrange(...)` predicate behind
 * `shift_feasibility_mv.availability_exception_ok`, so a shift the assignment
 * RPC would refuse produces a publish gate here and vice versa. An exception
 * that merely abuts the shift (ends exactly at the shift start) does not
 * overlap under `[)` and correctly does not fire.
 */
function isShiftCoveredByUnavailableException(
  exceptions: GateWorkerAvailabilityException[],
  shift: GateShift,
): GateWorkerAvailabilityException | null {
  if (!shift.staffId) return null;
  // Microsecond precision, not Date.parse: `timestamptz` separates instants
  // below the millisecond and flooring them would report no overlap where the
  // database sees one, silently dropping the gate.
  const shiftStart = parseInstantMicros(shift.startTime);
  const shiftEnd = parseInstantMicros(shift.endTime);
  if (shiftStart === null || shiftEnd === null) return null;
  for (const e of exceptions) {
    if (e.staffId !== shift.staffId) continue;
    if (e.status !== 'ACTIVE') continue;
    if (e.availabilityType !== 'UNAVAILABLE') continue;
    const start = parseInstantMicros(e.startsAt);
    const end = parseInstantMicros(e.endsAt);
    if (start === null || end === null) continue;
    // Empty/inverted ranges on either side overlap nothing — see
    // instantRangesOverlap. `roster_shifts` has no end_time > start_time CHECK
    // (only the assign RPC rejects it), so a degenerate shift row is reachable.
    if (instantRangesOverlap(start, end, shiftStart, shiftEnd)) return e;
  }
  return null;
}

function previousDateString(date: string): string {
  const d = new Date(date + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - 1);
  // eslint-disable-next-line statura/no-utc-date-string -- date arithmetic over YYYY-MM-DD keys only
  return d.toISOString().slice(0, 10);
}

function availabilityWindowMatchesDate(w: GateWorkerAvailability, date: string): boolean {
  if (date < w.effectiveFrom) return false;
  if (w.effectiveTo != null && date > w.effectiveTo) return false;
  if (w.pattern === 'RRULE') return false; // Phase 5 follow-up
  if (w.pattern === 'RECURRING_WEEKLY') {
    if (w.dayOfWeek == null) return false;
    return new Date(date + 'T00:00:00Z').getUTCDay() === w.dayOfWeek;
  }
  if (w.pattern === 'RECURRING_FORTNIGHTLY') {
    if (w.dayOfWeek == null) return false;
    const candidate = new Date(date + 'T00:00:00Z');
    const anchor = new Date(w.effectiveFrom + 'T00:00:00Z');
    const elapsedWeeks = Math.floor((candidate.getTime() - anchor.getTime()) / 604_800_000);
    return candidate.getUTCDay() === w.dayOfWeek && elapsedWeeks % 2 === 0;
  }
  if (w.pattern === 'RECURRING_MONTHLY') {
    return parseInt(date.slice(8, 10), 10) === parseInt(w.effectiveFrom.slice(8, 10), 10);
  }
  return true;
}

function isoMondayOf(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  const dow = d.getUTCDay(); // 0 = Sunday .. 6 = Saturday
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + offset);
  // eslint-disable-next-line statura/no-utc-date-string -- ISO week computation is timezone-agnostic
  return d.toISOString().slice(0, 10);
}

/**
 * Determine whether a worker's classification level satisfies the shift's
 * required classification. RN/EN/NP shifts require an RN-track classification.
 * Otherwise a numeric-rank match (HCA / SACS L1..L8) where worker rank >=
 * required rank.
 */
function classificationSatisfies(
  requirement: string,
  workerStream: string | null,
  workerLevel: string | null,
): boolean {
  const required = normaliseClassificationCode(requirement);
  const stream = normaliseClassificationCode(workerStream);
  const level = normaliseClassificationCode(workerLevel);
  const workerTokens = classificationTokens(workerStream, workerLevel);
  if (LEAD_RN_REQUIREMENTS.has(required)) {
    return isLeadRnClassification(workerStream, workerLevel, workerTokens);
  }
  if (RN_REQUIREMENTS.has(required)) {
    return isRnClassification(workerStream, workerLevel, workerTokens);
  }
  if (EN_REQUIREMENTS.has(required)) {
    return isEnClassification(workerStream, workerLevel, workerTokens);
  }
  if (LIFESTYLE_REQUIREMENTS.has(required)) {
    return isDirectCareClassification(workerTokens) || isLifestyleClassification(workerTokens);
  }
  if (DIRECT_CARE_REQUIREMENTS.has(required)) return isDirectCareClassification(workerTokens);
  if (SERVICE_REQUIREMENTS.has(required)) return isGeneralStaffServiceClassification(workerTokens);
  if (ADMIN_REQUIREMENTS.has(required)) return isGeneralStaffAdminClassification(workerTokens);
  if (required === stream || required === level) return true;
  const reqRank = HCA_RANK.get(required);
  const workerRank = HCA_RANK.get(level) ?? HCA_RANK.get(stream);
  if (reqRank == null || workerRank == null) return false;
  return workerRank >= reqRank;
}

/** Clinical scope is conservative: an unknown classification is never assumed pay-only. */
export function isClinicalRosterRequirement(
  shift: Pick<
    GateShift,
    'rosterRequirementKind' | 'requiredRole' | 'requiredClassification' | 'minimumScopeRole'
  >,
): boolean {
  if (shift.rosterRequirementKind === 'CLINICAL_SCOPE_MINIMUM') return true;
  return [shift.requiredRole, shift.requiredClassification, shift.minimumScopeRole].some((value) =>
    /(?:^|[^A-Z])(RN|RNAGY|LRN|EN|NP|NURSE|NURSING|CLINICAL|MIDWIFE|MIDWIFERY|AHP|ALLIED|PHYSIOTHERAPIST|OCCUPATIONAL_THERAPIST|SPEECH_PATHOLOGIST|PHARMACIST|MEDICAL|DOCTOR|PARAMEDIC|PSYCHOLOGIST|PODIATRIST|DIETITIAN|RADIOGRAPHER|DENTIST|OPTOMETRIST)(?:[^A-Z]|$)/i.test(
      (value ?? '').replace(/[\s-]+/g, '_'),
    ),
  );
}

export function rosterPublicationConsequence(
  ruleCode: RosterGateRuleCode,
  shift?: GateShift,
): 'PAY_HOLD' | 'CLINICAL_BLOCK' | 'EXISTING' {
  const kind = ROSTER_GATE_REGISTRY[ruleCode]!.publicationKind;
  if (kind === 'SCOPE_INTEGRITY') return 'CLINICAL_BLOCK';
  if (kind === 'PAY_ONLY') return 'PAY_HOLD';
  if (kind !== 'CLASSIFICATION_SCOPE') return 'EXISTING';
  if (!shift || isClinicalRosterRequirement(shift)) return 'CLINICAL_BLOCK';
  const industrial =
    shift.rosterRequirementKind === 'INDUSTRIAL_CLASSIFICATION_MINIMUM' ||
    (/^(?:L[1-8]|LEVEL[_ ]?[1-8]|GS[-_ ]|ACE[-_ ])/i.test(shift.requiredClassification ?? '') &&
      /(?:ADM|ADMIN|CATER|CLEAN|MAINTENANCE|GARDEN|LAUNDRY|COOK|CHEF|CLERICAL|RECEPTION|DRIVER)/i.test(
        shift.requiredRole ?? '',
      ));
  return industrial ? 'PAY_HOLD' : 'CLINICAL_BLOCK';
}

export function applyRosterPublishUnblock(gate: RosterGate, shift?: GateShift): RosterGate {
  const consequence = rosterPublicationConsequence(gate.ruleCode, shift);
  if (consequence === 'EXISTING') return gate;
  if (consequence === 'CLINICAL_BLOCK')
    return { ...gate, severity: 'BLOCK', publicationConsequence: consequence };
  return {
    ...gate,
    severity: gate.severity === 'BLOCK' ? 'WARN' : gate.severity,
    publicationConsequence: consequence,
    fixHref: '/settings/roster#prepare-pay-and-funding',
    suggestedAction:
      'Pay is held. Complete the worker pay setup and roster role mapping, then recompute pay context in Payroll readiness.',
  };
}

export function detectRosterGates(input: GateInput): RosterGate[] {
  const out: RosterGate[] = [];
  const leaveTimezone = input.timezone?.trim() || DEFAULT_TZ;
  const evaluatedAt = (input.periodMeta.evaluatedAt ?? new Date().toISOString()).slice(0, 10);
  const missingWorkerComplianceSeverity: GateSeverity =
    input.workerComplianceEvidenceMode === 'WARN' ? 'WARN' : 'BLOCK';
  const missingWorkerComplianceSuggestedAction =
    missingWorkerComplianceSeverity === 'BLOCK'
      ? 'Capture current evidence or reassign the shift before publishing.'
      : 'Capture current evidence through the provider backfill plan. Roster blocking begins when worker compliance enforcement is enabled.';
  const payProfileByStaffId = new Map(
    (input.workerPayProfiles ?? []).map((profile) => [profile.staffId, profile]),
  );
  const tenantPayRoleById = new Map((input.tenantPayRoles ?? []).map((role) => [role.id, role]));
  const staffPayRoleAssignments = input.staffPayRoleAssignments ?? [];
  const requiresMappedRosterRoles = (input.tenantPayRoles ?? []).some(
    (role) => normaliseStatus(role.status) === 'ACTIVE',
  );

  // Group shifts by worker for cross-shift rules.
  const byWorker = new Map<string, GateShift[]>();
  for (const s of input.shifts) {
    if (!s.staffId) continue;
    const list = byWorker.get(s.staffId) ?? [];
    list.push(s);
    byWorker.set(s.staffId, list);
  }
  // Order by parsed instant, never by timestamp text. `roster-chronology.ts`
  // documents why: '2026-07-20T09:00:00Z' and '...+00:00' are the same instant
  // but '+' (0x2B) sorts before 'Z' (0x5A), so a text sort silently reorders a
  // worker's day.
  //
  // This sort is load-bearing, not cosmetic. REST_BETWEEN_SHIFTS below walks
  // adjacent pairs of this list and measures
  // `hoursBetween(prev.endTime, curr.startTime)`. Reorder the pair and the gap
  // is measured across the wrong boundary, which yields a NEGATIVE difference,
  // which `Math.max(0, ...)` floors to 0 — and the `gapHours > 0` guard then
  // skips the pair entirely. So a text sort does not merely mis-order output:
  // it can silently suppress a genuine ten-hour-rest BLOCK.
  for (const list of byWorker.values())
    list.sort((a, b) => compareInstants(a.startTime, b.startTime));

  // ============================================================
  // Per-shift gates
  // ============================================================
  for (const s of input.shifts) {
    if (!s.staffId) continue; // Unassigned shifts are not subject to per-worker gates.

    // ASSIGNED_WORKER_TERMINATED
    const term = input.terminations.find((t) => t.staffId === s.staffId);
    // staff.end_date is the last working day (inclusive), matching the slot and
    // statutory assignment checks; only shifts after it are blocked.
    if (term?.terminatedAt && term.terminatedAt < s.date) {
      out.push({
        ruleCode: 'ASSIGNED_WORKER_TERMINATED',
        severity: 'BLOCK',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: `Shift assigned to worker terminated on ${term.terminatedAt}.`,
        suggestedAction: 'Reassign to an active worker before publishing.',
      });
    }

    const workRights = currentWorkRightsForWorker(input.workRights, s.staffId);
    if (workRights) {
      const status = normaliseStatus(workRights.status);
      const isExpired = workRights.expiresAt !== null && workRights.expiresAt < s.date;
      if (WORK_RIGHTS_KNOWN_BAD_STATUSES.has(status) || isExpired) {
        out.push({
          ruleCode: 'WORK_RIGHTS_EXPIRED',
          severity: 'BLOCK',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: workRights.expiresAt
            ? `Worker work-rights evidence expires on ${workRights.expiresAt}; renew or verify legal work rights before shift on ${s.date}.`
            : `Worker work-rights evidence is ${status}; renew or verify legal work rights before shift on ${s.date}.`,
          suggestedAction: 'Renew or verify legal work-rights evidence before publishing.',
          regulatory_class: 'WORK_RIGHTS',
        });
      }
    }

    const tenantPayRole = s.tenantPayRoleId ? tenantPayRoleById.get(s.tenantPayRoleId) : undefined;
    const roleRequirementKind = normaliseRosterRequirementKind(
      s.rosterRequirementKind ?? tenantPayRole?.rosterRequirementKind,
    );
    const minimumScopeRole = s.minimumScopeRole ?? tenantPayRole?.minimumScopeRole ?? null;
    const clinicalScopeRequirement = isClinicalRosterRequirement({
      ...s,
      rosterRequirementKind: roleRequirementKind,
      minimumScopeRole,
    });
    const payRoleAssignment =
      s.tenantPayRoleId && tenantPayRole
        ? activeEligiblePayRoleAssignment(staffPayRoleAssignments, s, s.tenantPayRoleId)
        : null;
    const activePayRoleContext = Boolean(
      s.tenantPayRoleId &&
      tenantPayRole &&
      normaliseStatus(tenantPayRole.status) === 'ACTIVE' &&
      payRoleAssignment,
    );
    const payRoleSuppliesHourlyAward = Boolean(
      activePayRoleContext &&
      tenantPayRole?.baseAwardCode &&
      tenantPayRole.baseAwardCode !== 'UNCLASSIFIED' &&
      normaliseStatus(tenantPayRole.roleKind ?? '') !== 'SALARY_CONTEXT' &&
      roleRequirementKind !== 'SALARY_CONTEXT',
    );
    const resolvedSalaryFlatContext = hasResolvedSalaryFlatContext(s);

    if (
      s.payContextStatus &&
      normaliseStatus(s.payContextStatus) !== 'RESOLVED' &&
      (Boolean(s.tenantPayRoleId) || !s.requiredRole || !requiresMappedRosterRoles)
    ) {
      out.push({
        ruleCode: 'PAY_CONTEXT_UNRESOLVED',
        severity: 'BLOCK',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: `Assignment pay context is ${normaliseStatus(s.payContextStatus)} for ${s.date}.`,
        suggestedAction:
          'Resolve the assignment pay role and all pay-context blockers before publishing.',
      });
    }

    if (
      !s.tenantPayRoleId &&
      s.requiredRole &&
      requiresMappedRosterRoles &&
      !resolvedSalaryFlatContext
    ) {
      out.push({
        ruleCode: 'PAY_ROLE_MAPPING_REQUIRED',
        severity: 'BLOCK',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: `Roster role ${s.requiredRole} is not mapped to a tenant pay role for ${s.date}.`,
        suggestedAction:
          'Map this roster role in payroll settings before publishing. Multi-role payroll cannot infer pay context from a staff default.',
      });
    } else if (s.tenantPayRoleId && !tenantPayRole) {
      out.push({
        ruleCode: 'PAY_ROLE_MAPPING_INCOMPLETE',
        severity: 'BLOCK',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: `Roster role is mapped to pay role ${s.tenantPayRoleId}, but that pay role was not loaded for ${s.date}.`,
        suggestedAction:
          'Refresh the roster role mapping/pay-role catalogue before publishing. Do not publish with an unresolved pay role.',
      });
    } else if (tenantPayRole && normaliseStatus(tenantPayRole.status) !== 'ACTIVE') {
      out.push({
        ruleCode: 'PAY_ROLE_NOT_ACTIVE',
        severity: 'BLOCK',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: `Roster role ${s.requiredRole ?? s.requiredClassification ?? tenantPayRole.code} maps to inactive pay role ${tenantPayRole.code}.`,
        suggestedAction: 'Activate the mapped pay role or remap the roster role before publishing.',
      });
    } else if (s.tenantPayRoleId && tenantPayRole && !payRoleAssignment) {
      out.push({
        ruleCode: 'PAY_ROLE_ELIGIBILITY_MISSING',
        severity: 'BLOCK',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: `Worker is not actively eligible for pay role ${tenantPayRole.code} on ${s.date}.`,
        suggestedAction:
          'Assign and approve the worker for this pay role, choose a different role, or reassign the shift.',
      });
    } else if (s.tenantPayRoleId && tenantPayRole && roleRequirementKind === null) {
      out.push({
        ruleCode: 'PAY_ROLE_REQUIREMENT_KIND_UNKNOWN',
        severity: 'BLOCK',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: `Roster role ${s.requiredRole ?? s.requiredClassification ?? tenantPayRole.code} has an unknown roster requirement kind.`,
        suggestedAction:
          'Correct the roster role mapping/pay-role requirement kind before publishing. Unknown role requirements cannot be treated as role tags.',
      });
    }

    // MISSING_CLASSIFICATION
    const classification = input.classifications.find(
      (c) => c.staffId === s.staffId && classificationCovers(c, s.date),
    );
    const nonHourlyPayProfile = isNonHourlyPayProfile(payProfileByStaffId.get(s.staffId));
    if (!classification && !payRoleSuppliesHourlyAward) {
      if (nonHourlyPayProfile && !(input.publishUnblockEnabled && resolvedSalaryFlatContext)) {
        out.push({
          ruleCode: 'NON_HOURLY_PAY_PROFILE_MANUAL_REVIEW',
          severity: 'WARN',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Worker has a non-hourly pay profile and no hourly classification history covering ${s.date}.`,
          suggestedAction:
            'Confirm the salary/common-law pay treatment before pay-run finalisation; no hourly EA rate will be auto-applied for this shift.',
        });
      } else if (!nonHourlyPayProfile) {
        out.push({
          ruleCode: 'MISSING_CLASSIFICATION',
          severity: 'BLOCK',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `No classification history covers ${s.date} for this worker.`,
          suggestedAction: 'Set a classification effective on or before the shift date.',
        });
      }
    } else if (
      classification &&
      (!classification.awardCode || classification.awardCode === 'UNCLASSIFIED') &&
      !payRoleSuppliesHourlyAward
    ) {
      if (nonHourlyPayProfile && !(input.publishUnblockEnabled && resolvedSalaryFlatContext)) {
        out.push({
          ruleCode: 'NON_HOURLY_PAY_PROFILE_MANUAL_REVIEW',
          severity: 'WARN',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Worker has a non-hourly pay profile and no canonical hourly award_code on ${s.date}.`,
          suggestedAction:
            'Confirm the salary/common-law pay treatment before pay-run finalisation; no hourly EA rate will be auto-applied for this shift.',
        });
      } else if (!nonHourlyPayProfile) {
        out.push({
          ruleCode: 'MISSING_CLASSIFICATION',
          severity: 'BLOCK',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Worker has no canonical award_code on ${s.date} (got '${classification.awardCode ?? 'NULL'}').`,
          suggestedAction: 'Correct the worker classification to a SCHADS / Nurses / AHP award.',
        });
      }
    }

    // RATE_VERSION_MISSING
    const awardCodeForRateCheck = payRoleSuppliesHourlyAward
      ? tenantPayRole?.baseAwardCode
      : classification?.awardCode;
    if (awardCodeForRateCheck && awardCodeForRateCheck !== 'UNCLASSIFIED' && !nonHourlyPayProfile) {
      const hasRate = input.rateVersions.some((r) => {
        const rateAward = normaliseClassificationCode(r.awardCode);
        return (
          rateAward === normaliseClassificationCode(awardCodeForRateCheck) && rateCovers(r, s.date)
        );
      });
      if (!hasRate) {
        out.push({
          ruleCode: 'RATE_VERSION_MISSING',
          severity: 'BLOCK',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `No active rate version for award ${awardCodeForRateCheck} on ${s.date}.`,
          suggestedAction: "Seed an effective-dated rate version for the worker's award.",
        });
      }
    }

    // INSUFFICIENT_CLASSIFICATION
    const classificationRequirements = new Map<string, string>();
    const addClassificationRequirement = (requirement: string | null | undefined) => {
      const normalised = normaliseClassificationCode(requirement ?? null);
      if (normalised && !classificationRequirements.has(normalised)) {
        classificationRequirements.set(normalised, requirement!);
      }
    };
    if (!activePayRoleContext || (input.publishUnblockEnabled && clinicalScopeRequirement)) {
      addClassificationRequirement(s.requiredClassification);
    } else if (
      s.requiredClassification &&
      !payRoleLabelCoversRequirement(tenantPayRole, s.requiredClassification)
    ) {
      addClassificationRequirement(s.requiredClassification);
    }
    if (
      activePayRoleContext &&
      (roleRequirementKind === 'CLINICAL_SCOPE_MINIMUM' ||
        roleRequirementKind === 'INDUSTRIAL_CLASSIFICATION_MINIMUM')
    ) {
      addClassificationRequirement(minimumScopeRole);
    }

    if (input.publishUnblockEnabled && clinicalScopeRequirement) {
      // Industrial grade metadata cannot stand in for the clinical role itself.
      // In particular a high GS grade must never satisfy an RN-only assignment.
      const clinicalRole = [minimumScopeRole, s.requiredRole, s.requiredClassification].find(
        (role) => role && isClinicalRosterRequirement({ requiredRole: role }),
      );
      if (clinicalRole) {
        const roleCode = normaliseClassificationCode(clinicalRole);
        const requirement = LEAD_RN_REQUIREMENTS.has(roleCode)
          ? 'LEAD_RN'
          : RN_REQUIREMENTS.has(roleCode)
            ? 'RN'
            : EN_REQUIREMENTS.has(roleCode)
              ? 'EN'
              : clinicalRole;
        addClassificationRequirement(requirement);
      } else {
        // Explicit clinical kind with no declared minimum remains a repair stop.
        addClassificationRequirement('CLINICAL_SCOPE_MINIMUM');
      }
    }
    for (const minimumClassificationRequirement of classificationRequirements.values()) {
      if (nonHourlyPayProfile && !(input.publishUnblockEnabled && clinicalScopeRequirement))
        continue;
      if (!classification) {
        out.push({
          ruleCode: 'INSUFFICIENT_CLASSIFICATION',
          severity: 'BLOCK',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Shift requires ${minimumClassificationRequirement}; worker has no classification/scope evidence covering ${s.date}.`,
          suggestedAction:
            'Capture the required classification/scope evidence or reassign to an appropriately-scoped worker.',
        });
      } else if (
        !classificationSatisfies(
          minimumClassificationRequirement,
          classification.classificationStream,
          classification.classificationLevel,
        )
      ) {
        out.push({
          ruleCode: 'INSUFFICIENT_CLASSIFICATION',
          severity: 'BLOCK',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Shift requires ${minimumClassificationRequirement}; worker is ${classification.classificationStream ?? '?'}/${classification.classificationLevel ?? '?'}.`,
          suggestedAction: 'Reassign to an appropriately-classified worker.',
        });
      }
    }

    // MISSING_SCREENING / EXPIRED_SCREENING / AHPRA_REGISTRATION_*
    //
    // Statutory floor first: every direct-care assignment needs a current
    // police-check / worker-screening clearance, independent of whether the
    // slot named a requirement and independent of worker_compliance mode.
    // Provider-configured extras still run below as requirement-driven checks.
    const workerScreenings = input.screenings.filter((x) => x.staffId === s.staffId);
    // Pass staffRole + serviceCode: SQL already uses v_staff_role / service_code.
    // Without them, Medea-style lines (vague hour-range service_type, empty
    // required_role, PCA/RN worker) look like non-care and the floor never fires.
    const floor = evaluateStatutoryScreeningFloor({
      isDirectCare: isDirectCareAssignment({
        serviceType: s.serviceType,
        serviceCode: s.serviceCode ?? null,
        requiredRole: s.requiredRole,
        staffRole: s.staffRole ?? null,
      }),
      shiftDate: s.date,
      screenings: workerScreenings.map((sc) => ({
        screeningType: sc.screeningType,
        status: sc.status,
        expiresAt: sc.expiresAt,
      })),
    });
    if (floor.status === 'MISSING') {
      out.push({
        ruleCode: 'MISSING_SCREENING',
        /*
           Mode-governed, NOT a hardcoded block.

           This fires on the ABSENCE of a screening record, and absence is the
           default for a provider still onboarding evidence. Measured on
           production: Medea Park 87 of 87 active workers with no screening row,
           Golden Bloom 14 of 16. A hardcoded BLOCK here would have refused every
           direct-care assignment and publish at both live providers — stopping
           care delivery rather than improving compliance.

           Under WARN the gap is still detected, recorded and visible; it does
           not block. It becomes a BLOCK when the provider moves to
           ENFORCE_ROSTER / ENFORCE_ALL, with the attestation runway as the exit.
           EXPIRED_SCREENING below stays hard: nothing can be expired on a tenant
           that has recorded nothing, so it cannot fire on an empty tenant.
        */
        severity: missingWorkerComplianceSeverity,
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: `Capture a national police check or worker screening clearance before direct-care shift on ${s.date}.`,
        suggestedAction: missingWorkerComplianceSuggestedAction,
        regulatory_class: 'SCREENING',
      });
    } else if (floor.status === 'EXPIRED_OR_INVALID') {
      out.push({
        ruleCode: 'EXPIRED_SCREENING',
        severity: 'BLOCK',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: floor.awaitingVerification
          ? `The ${screeningLabel(floor.typeLabel)} is awaiting second-person verification — verify it before shift on ${s.date}.`
          : `Renew the ${screeningLabel(floor.typeLabel)} before shift on ${s.date}.`,
        suggestedAction: 'Renew or verify the screening evidence before publishing.',
        regulatory_class: 'SCREENING',
      });
    }

    if (input.clinicalRegistrationAssessmentEnabled) {
      // Explicit non-clinical work wins over an unrelated substantive nursing role.
      const explicitFunction = [
        minimumScopeRole,
        s.requiredRole,
        s.requiredClassification,
        tenantPayRole?.code,
      ].find((value) => Boolean(value?.trim()));
      const actualFunction =
        explicitFunction ??
        (nursingRegistrationFamily(s.serviceType) ? s.serviceType : s.staffRole);
      const family = nursingRegistrationFamily(actualFunction);
      const assessment = input.clinicalRegistrationAssessments?.find(
        (row) => row.staffId === s.staffId && row.date === s.date,
      );
      if (family && !(family === 'RN' ? assessment?.currentRn : assessment?.currentEn)) {
        out.push({
          ruleCode: 'AHPRA_REGISTRATION_REVIEW_REQUIRED',
          severity: 'WARN',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Review independently verified ${family} registration for the clinical function on ${s.date}.`,
          suggestedAction:
            'Open the worker screening register and review registration evidence. Registry checks are manual.',
          regulatory_class: 'AHPRA_REGISTRATION',
        });
      }
    }

    const required = uniqueCodes(s.requiredScreenings, tenantPayRole?.screeningRequirements).filter(
      (r) => SCREENING_TYPES_REQUIRED.has(r),
    );
    for (const reqType of required) {
      const sc = workerScreenings.find((x) => x.screeningType === reqType);
      const isAhpra = reqType === 'AHPRA_REGISTRATION';
      const regulatoryClass: ComplianceRegulatoryClass = isAhpra
        ? 'AHPRA_REGISTRATION'
        : 'SCREENING';
      if (!sc) {
        // Floor already raised MISSING for statutory types on direct care —
        // do not double-count the same absence under a second label.
        if (
          floor.status === 'MISSING' &&
          (reqType === 'NATIONAL_POLICE_CHECK' ||
            reqType === 'NDIS_WORKER_SCREENING' ||
            reqType === 'AGED_CARE_WORKER_SCREENING')
        ) {
          continue;
        }
        out.push({
          ruleCode: isAhpra ? 'AHPRA_REGISTRATION_MISSING' : 'MISSING_SCREENING',
          severity: isAhpra ? 'BLOCK' : missingWorkerComplianceSeverity,
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Capture a ${screeningLabel(reqType)} record for shift on ${s.date}.`,
          suggestedAction: missingWorkerComplianceSuggestedAction,
          regulatory_class: regulatoryClass,
        });
        continue;
      }
      const knownBad =
        SCREENING_KNOWN_BAD_STATUSES.has(normaliseStatus(sc.status)) ||
        (isAhpra &&
          AHPRA_KNOWN_BAD_VERIFICATION_STATUSES.has(
            normaliseStatus(sc.ahpraVerificationStatus ?? ''),
          )) ||
        (sc.expiresAt !== null && sc.expiresAt < s.date);
      if (knownBad) {
        // REQUIRES_REVIEW keeps the EXPIRED_SCREENING rule code (consumers —
        // non-overridable-gates + the COMPLIANCE_RULE_CODES registry — match
        // on it) but gets an accurate awaiting-verification message unless the
        // record is also date-expired.
        const awaitingVerification =
          normaliseStatus(sc.status) === 'REQUIRES_REVIEW' &&
          !(sc.expiresAt !== null && sc.expiresAt < s.date);
        // Floor already raised EXPIRED for the same statutory type — skip dupe.
        if (
          floor.status === 'EXPIRED_OR_INVALID' &&
          !isAhpra &&
          (reqType === 'NATIONAL_POLICE_CHECK' ||
            reqType === 'NDIS_WORKER_SCREENING' ||
            reqType === 'AGED_CARE_WORKER_SCREENING')
        ) {
          continue;
        }
        out.push({
          ruleCode: isAhpra ? 'AHPRA_REGISTRATION_EXPIRED' : 'EXPIRED_SCREENING',
          severity: 'BLOCK',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: awaitingVerification
            ? `The ${screeningLabel(reqType)} is awaiting second-person verification — verify it before shift on ${s.date}.`
            : `Renew the ${screeningLabel(reqType)} before shift on ${s.date}.`,
          suggestedAction: 'Renew or verify the screening evidence before publishing.',
          regulatory_class: regulatoryClass,
        });
      }
    }

    // MISSING_REQUIRED_TRAINING / EXPIRED_REQUIRED_TRAINING
    for (const trCode of uniqueCodes(s.requiredTraining, tenantPayRole?.trainingRequirements)) {
      const requiredCode = normaliseTrainingCode(trCode);
      const tr = selectTrainingRecordForRequirement(
        input.training,
        s.staffId,
        requiredCode,
        s.date,
      );
      if (!tr) {
        out.push({
          ruleCode: 'MISSING_REQUIRED_TRAINING',
          severity: 'WARN',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Capture a completed ${trCode} training record before shift on ${s.date}.`,
          suggestedAction:
            'Capture training evidence. A manager override is needed before first assignment only if this requirement is configured for it.',
          regulatory_class: 'TRAINING',
        });
        continue;
      }
      const status = normaliseStatus(tr.status);
      const knownBad =
        TRAINING_KNOWN_BAD_STATUSES.has(status) || (tr.expiresAt !== null && tr.expiresAt < s.date);
      if (knownBad) {
        out.push({
          ruleCode: 'EXPIRED_REQUIRED_TRAINING',
          severity: 'WARN',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Renew the ${trCode} training before shift on ${s.date}.`,
          suggestedAction:
            'Renew or verify training evidence. A manager override is needed before first assignment only if this requirement is configured for it.',
          regulatory_class: 'TRAINING',
        });
      } else if (status !== 'COMPLETED') {
        out.push({
          ruleCode: 'MISSING_REQUIRED_TRAINING',
          severity: 'WARN',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Capture a completed ${trCode} training record before shift on ${s.date}.`,
          suggestedAction:
            'Capture training evidence. A manager override is needed before first assignment only if this requirement is configured for it.',
          regulatory_class: 'TRAINING',
        });
      }
    }

    // WORKER_ON_LEAVE
    //
    // RCA-1.2. Absence decides WHETHER the gate fires; payability decides only
    // how hard. Approved-but-unpayable leave still means the worker is away —
    // "not payable" is a statement about money and never a statement that
    // someone is at work.
    const onLeave = input.leave.find((l) => approvedLeaveCoversShift(l, s, leaveTimezone));
    if (onLeave) {
      const payableUnresolved = onLeave.payable === false;
      out.push({
        ruleCode: 'WORKER_ON_LEAVE',
        // Owner decision D5: the warning ships BEFORE the block, so no existing
        // assignment is wedged before anyone has seen it.
        severity: payableUnresolved ? 'WARN' : 'BLOCK',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: payableUnresolved
          ? `Worker has APPROVED leave on ${s.date}. Pay treatment is unresolved, so this is a warning rather than a block.`
          : `Worker has APPROVED leave on ${s.date}.`,
        suggestedAction: 'Reassign or rescind the leave before publishing.',
      });
    }

    // WORKER_NOT_AVAILABLE — Phase 4 Component 16. Worker has declared
    // UNAVAILABLE for the shift's date / time-of-day. WARN by default per
    // spec; tunable to BLOCK per tenant in a follow-up.
    //
    // Two independent sources, both of which the assignment RPCs already treat
    // as hard blockers (`availability_pattern` / `availability_exception`):
    //   1. recurring patterns + legacy declarations (`input.availability`)
    //   2. one-off exceptions (`input.availabilityExceptions`)
    // A shift assigned BEFORE an absence was recorded skips the RPC block
    // entirely, so publish is the only place it can still be caught. Emit at
    // most one row per shift — the source does not change the operator's fix.
    const declaredPattern =
      input.availability && input.availability.length > 0
        ? isShiftCoveredByUnavailableWindow(input.availability, s)
        : null;
    const declaredException =
      !declaredPattern && input.availabilityExceptions && input.availabilityExceptions.length > 0
        ? isShiftCoveredByUnavailableException(input.availabilityExceptions, s)
        : null;
    const declared = declaredPattern ?? declaredException;
    if (declared) {
      out.push({
        ruleCode: 'WORKER_NOT_AVAILABLE',
        severity: 'WARN',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: `Worker has declared UNAVAILABLE on ${s.date} (declaration ${declared.id}).`,
        suggestedAction:
          "Reassign, override with reason, or update the worker's availability declaration.",
      });
    }

    // EXCESSIVE_DAILY_HOURS — > 12h in single shift without explicit allowance.
    const dailyHours = shiftPaidHours(s);
    if (dailyHours > 12 && !s.excessHoursAllowance) {
      out.push({
        ruleCode: 'EXCESSIVE_DAILY_HOURS',
        severity: 'BLOCK',
        scope: 'SHIFT',
        scopeRefType: gateScopeRefType(s),
        scopeRef: gateScopeRef(s),
        message: `Shift on ${s.date} is ${dailyHours.toFixed(2)}h — exceeds 12h daily limit.`,
        suggestedAction: 'Split the shift, or capture an excess-hours allowance with reason.',
      });
    }

    // MIN_ENGAGEMENT_BREACH — SCHADS cl 10.5(b) advisory only when ALL of:
    // award MA000100 + PT/casual employment + home-care service cohort.
    // Full-time has no Award per-shift floor under cl 10.5 (S16 / Cluster E1 errata #26).
    // Profile/EA floors are enforced at timesheet top-up; this gate is award-default advisory.
    if (classification) {
      const employment = (classification.employmentType ?? '').toUpperCase();
      const award = (classification.awardCode ?? '').toUpperCase();
      const isFullTime = employment === 'FULL_TIME';
      const isSchads = award === 'MA000100';
      const isPtOrCasual =
        employment === 'CASUAL' ||
        employment === 'PART_TIME' ||
        employment === 'PART-TIME' ||
        employment === 'PERMANENT';
      const serviceType = (s.serviceType ?? '').toUpperCase();
      // Positive home-care service signal required (R15). Residential/RAC types excluded.
      const isResidentialService = /RESIDENTIAL|RAC_|WING_|CARE_MINUTES|AN_ACC|BED_/i.test(
        serviceType,
      );
      const isHomeCareCohort =
        !isResidentialService &&
        /PERSONAL_CARE|DOMESTIC|HOME_CARE|SOCIAL_SUPPORT|CLINICAL_CARE|NURSING|ALLIED|TRANSPORT|RESPITE|MEAL|GARDEN|HOME_MAINTENANCE|COMMUNITY|SAH|HCP/i.test(
          serviceType,
        );
      const minHours = isSchads && !isFullTime && isPtOrCasual && isHomeCareCohort ? 2 : 0;
      if (minHours > 0 && dailyHours > 0 && dailyHours < minHours) {
        out.push({
          ruleCode: 'MIN_ENGAGEMENT_BREACH',
          severity: 'WARN',
          scope: 'SHIFT',
          scopeRefType: gateScopeRefType(s),
          scopeRef: gateScopeRef(s),
          message: `Shift on ${s.date} is ${dailyHours.toFixed(2)}h — below SCHADS minimum engagement (${minHours}h ${employment === 'CASUAL' ? 'casual' : 'part-time/home care'}, cl 10.5(b)).`,
          suggestedAction:
            'Pad to minimum engagement (SCHADS cl 10.5(b) for PT/casual home care), or note EA/profile exemption. Full-time has no Award per-shift floor. Advisory is award + employment + home-care cohort gated.',
        });
      }
    }
  }

  // ============================================================
  // Per-worker per-period gates
  // ============================================================
  for (const [staffId, list] of byWorker.entries()) {
    // DOUBLE_BOOKING — overlapping shifts. Emit a gate against BOTH shifts
    // so the manager queue surfaces the conflict on either shift's row;
    // resolving one without inspecting the other could leave a fresh
    // overlap with a third shift.
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i]!;
        const b = list[j]!;
        // Half-open instant intersection, not text comparison. The predicate
        // shape was already correct — touching shifts (one ends exactly when
        // the next begins) are NOT a double-booking and must not fire — but it
        // was applied to raw ISO strings, so two spellings of the same instant
        // ('...Z' vs '...+00:00'), or two different UTC offsets, compared as
        // characters rather than times.
        //
        // `instantRangesOverlap` is the same helper this file already uses for
        // the availability-exception gate, and it mirrors the database's
        // `tstzrange(..., '[)') && tstzrange(..., '[)')`, so a shift the
        // assignment RPC would refuse produces a publish gate here and vice
        // versa. DOUBLE_BOOKING is severity BLOCK and neverSoften, so a
        // divergence is either a roster that cannot be published for a reason
        // the database does not agree with, or one that publishes despite a
        // real clash.
        const aStart = parseInstantMicros(a.startTime);
        const aEnd = parseInstantMicros(a.endTime);
        const bStart = parseInstantMicros(b.startTime);
        const bEnd = parseInstantMicros(b.endTime);
        if (
          aStart !== null &&
          aEnd !== null &&
          bStart !== null &&
          bEnd !== null &&
          instantRangesOverlap(aStart, aEnd, bStart, bEnd)
        ) {
          for (const target of [a, b] as const) {
            const other = target.id === a.id ? b : a;
            out.push({
              ruleCode: 'DOUBLE_BOOKING',
              severity: 'BLOCK',
              scope: 'SHIFT',
              scopeRefType: gateScopeRefType(target),
              scopeRef: gateScopeRef(target),
              message: `Shift overlaps with another shift (${other.startTime} → ${other.endTime}).`,
              suggestedAction: 'Reassign or reschedule one of the overlapping shifts.',
            });
          }
        }
      }
    }

    // REST_BETWEEN_SHIFTS — use the worker's event pay-role agreement.
    // The prior generic SCHADS 10-hour rule produced false blockers for Medea
    // General Staff and understated the distinct Nurses EA source contract.
    for (let i = 1; i < list.length; i++) {
      const prev = list[i - 1]!;
      const curr = list[i]!;

      // MA000100 cl 25.6(g) places the ten-hour rest "between broken shifts
      // rostered on SUCCESSIVE DAYS". Two home-care visits on the same day are
      // periods of work within ONE broken shift (cl 25.6(a)(i) / (b)(i)) — not
      // two shifts with no rest between them. Measuring the gap between them
      // reported a rest-break breach for every consecutive visit: 126 on Golden
      // Bloom, of which live measurement showed ZERO were real cross-day
      // breaches. Coordinators cleared them by override, which is how an
      // override stops meaning anything.
      //
      // This mirrors the exemption already carried by
      // roster_v2_slot_assignment_hard_blockers, so the publish gate and the
      // assignment blocker finally agree. cl 25.6 is limited to home care and
      // SACS disability services employees.
      //
      // Also treat null serviceContext + null facility as home-care style when
      // neither side is explicitly RESIDENTIAL — client_id can be missing on
      // legacy/admin shifts and must not reintroduce visit-to-visit false
      // BLOCKs for pure home-care tenants.
      const prevIsResidential = prev.serviceContext === 'RESIDENTIAL';
      const currIsResidential = curr.serviceContext === 'RESIDENTIAL';
      const prevIsHomeCareStyle =
        prev.serviceContext === 'HOME_CARE' ||
        (prev.serviceContext == null && !prev.facilityId && !prevIsResidential);
      const currIsHomeCareStyle =
        curr.serviceContext === 'HOME_CARE' ||
        (curr.serviceContext == null && !curr.facilityId && !currIsResidential);
      if (prevIsHomeCareStyle && currIsHomeCareStyle && prev.date === curr.date) {
        continue;
      }

      const wrapGapHours = hoursBetween(prev.endTime, curr.startTime);
      if (
        isSleepoverFlankSameShift({
          prevHasSleepover: Boolean(prev.hasSleepover),
          currHasSleepover: Boolean(curr.hasSleepover),
          laterShiftDateYmd: curr.date,
          gapHours: wrapGapHours,
        })
      ) {
        continue;
      }
      if (prev.isRemoteWork || curr.isRemoteWork) {
        continue;
      }

      const gapHours = wrapGapHours;
      const currPayRole = curr.tenantPayRoleId
        ? tenantPayRoleById.get(curr.tenantPayRoleId)
        : undefined;
      const currClassification = input.classifications.find(
        (classification) =>
          classification.staffId === staffId && classificationCovers(classification, curr.date),
      );
      const awardCode = normaliseClassificationCode(
        currPayRole?.baseAwardCode ?? currClassification?.awardCode ?? '',
      );
      const isMedeaGeneralStaff = awardCode === 'MA000018' || awardCode === 'GENERAL_STAFF_EA';
      const isMedeaNurse = awardCode === 'MA000034' || awardCode === 'NURSES_EA';
      const minimumGapHours = isMedeaGeneralStaff ? 8 : isMedeaNurse ? 9 : 10;
      if (gapHours > 0 && gapHours < minimumGapHours) {
        // Citation drift, same class as the 14 corrected on 2026-05-16: cl 28.5
        // is "Rest break during overtime" (meal breaks while working overtime)
        // and says nothing about a 10-hour break. The generic rule is
        // cl 25.4(a) — "a break of not less than 10 hours between the end of
        // one shift and the beginning of the next". For home-care workers the
        // more specific cl 25.6(g) governs, measured between broken shifts on
        // successive days.
        // cl 25.6(g) governs the rest between BROKEN SHIFTS. A day worked as one
        // continuous home-care visit is not a broken shift, so citing 25.6(g)
        // there would be over-specific — the generic cl 25.4(a) is the rule. Only
        // claim the broken-shift clause when a day actually has more than one
        // period of work.
        const homeCarePeriodsOn = (d: string) =>
          list.filter((s) => s.date === d && s.serviceContext === 'HOME_CARE').length;
        const isHomeCareSuccessiveDay =
          prev.serviceContext === 'HOME_CARE' &&
          curr.serviceContext === 'HOME_CARE' &&
          (homeCarePeriodsOn(prev.date) > 1 || homeCarePeriodsOn(curr.date) > 1);
        const message = isMedeaGeneralStaff
          ? `Only ${gapHours.toFixed(2)}h between shifts — Medea General Staff EA cl 32(g) requires at least 8h rest after overtime.`
          : isMedeaNurse
            ? `Only ${gapHours.toFixed(2)}h between shifts — Medea Nurses EA cl 13.5 requires at least 9h rest.`
            : isHomeCareSuccessiveDay
              ? `Only ${gapHours.toFixed(2)}h since the previous day's work — SCHADS cl 25.6(g) requires a 10h break between broken shifts rostered on successive days.`
              : `Only ${gapHours.toFixed(2)}h between shifts — SCHADS cl 25.4(a) requires a 10h break between the end of one shift and the start of the next.`;
        out.push({
          ruleCode: 'TEN_HOUR_BREAK',
          severity: 'BLOCK',
          scope: 'WORKER_PERIOD',
          scopeRefType: gateScopeRefType(curr),
          scopeRef: gateScopeRef(curr),
          message,
          suggestedAction: `Reschedule the current or previous shift to ensure at least ${minimumGapHours}h rest.`,
        });
      }
    }

    // BROKEN_SHIFT_SPAN_EXCEEDED — cl 25.6(f): "The span of hours for a broken
    // shift is up to 12 hours. All work performed beyond a span of 12 hours will
    // be paid at double time."
    //
    // Measured across the whole day's home-care work, first start to last end,
    // because that is what "the span of a broken shift" means. This is the
    // control that the cl 25.6(g) unit fix above makes necessary: exempting
    // same-day pairs from the rest rule removes the incidental signal that a
    // 06:00-14:00 + 20:00-23:59 day used to produce. A long span is the thing
    // genuinely worth surfacing there, and it carries a double-time cost the
    // provider should see before publishing rather than discover in the pay run.
    const homeCareDaySpans = new Map<string, { start: string; end: string }>();
    for (const s of list) {
      if (s.serviceContext !== 'HOME_CARE') continue;
      const seen = homeCareDaySpans.get(s.date);
      if (!seen) {
        homeCareDaySpans.set(s.date, { start: s.startTime, end: s.endTime });
        continue;
      }
      // Instant min/max, not string min/max. `hoursBetween(span.start, span.end)`
      // below turns these two into the broken-shift span measured against the
      // SCHADS cl 25.6(f) 12-hour cap, beyond which work is paid at double time.
      // A text min/max picks the extremes by punctuation, so a day whose rows
      // spell instants differently could report a span that no pair of its own
      // visits actually describes — under-reporting the cap as easily as over.
      if (compareInstants(s.startTime, seen.start) < 0) seen.start = s.startTime;
      if (compareInstants(s.endTime, seen.end) > 0) seen.end = s.endTime;
    }
    for (const [date, span] of homeCareDaySpans.entries()) {
      const spanHours = hoursBetween(span.start, span.end);
      if (spanHours <= 12) continue;
      const anchor = list.find((s) => s.date === date && s.serviceContext === 'HOME_CARE');
      if (!anchor) continue;
      out.push({
        ruleCode: 'BROKEN_SHIFT_SPAN_EXCEEDED',
        severity: 'WARN',
        scope: 'WORKER_PERIOD',
        scopeRefType: gateScopeRefType(anchor),
        scopeRef: gateScopeRef(anchor),
        message: `Home-care work on ${date} spans ${spanHours.toFixed(2)}h — SCHADS cl 25.6(f) caps a broken shift at 12h, and work beyond the span is paid at double time.`,
        suggestedAction:
          'Shorten the span or accept the double-time cost for work beyond 12 hours.',
      });
    }

    // EXCESSIVE_WEEKLY_HOURS — > 38h/week for permanent workers (cl 25).
    // Classification is re-evaluated per ISO week (the worker's effective-dated
    // history may transition mid-period, e.g. casual → part-time).
    const byWeek = new Map<string, number>();
    for (const s of list) {
      const wk = isoMondayOf(s.date);
      byWeek.set(wk, (byWeek.get(wk) ?? 0) + shiftPaidHours(s));
    }
    for (const [wk, hours] of byWeek.entries()) {
      if (hours <= 38) continue;
      const classificationForWeek = input.classifications.find(
        (c) => c.staffId === staffId && classificationCovers(c, wk),
      );
      const isPermanent =
        classificationForWeek?.employmentType === 'FULL_TIME' ||
        classificationForWeek?.employmentType === 'PART_TIME';
      if (!isPermanent) continue;
      out.push({
        ruleCode: 'EXCESSIVE_WEEKLY_HOURS',
        severity: 'WARN',
        scope: 'WORKER_PERIOD',
        scopeRefType: 'worker_week',
        scopeRef: `${staffId}:${wk}`,
        message: `${hours.toFixed(2)}h scheduled in week of ${wk} for permanent worker.`,
        suggestedAction: 'Reduce hours or capture a 10-hour-agreement before publishing.',
      });
    }
  }

  // ============================================================
  // Per-period gates
  // ============================================================

  // LATE_PUBLISH_NOTICE — < 7 days notice (cl 25.5(c)).
  const noticeDays = dayDiff(evaluatedAt, input.periodMeta.periodStart);
  if (noticeDays < 7) {
    out.push({
      ruleCode: 'LATE_PUBLISH_NOTICE',
      severity: 'WARN',
      scope: 'PERIOD',
      scopeRefType: 'period',
      scopeRef: input.periodMeta.rosterPeriodId,
      message: `Period would be published with ${noticeDays} day(s) notice (SCHADS cl 25.5(c) requires 7).`,
      suggestedAction: 'Publish with override + reason, or delay publish by a few days.',
    });
  }

  // BUDGET_BREACH_ESTIMATE — Phase 4 Component 19. Fires INFO when the
  // estimated total wages exceed period budget by > 5%. Caller computes
  // the estimate via `estimateRosterCost` and passes both numbers in.
  if (input.estimatedTotalWages != null && input.periodBudget != null) {
    // eslint-disable-next-line statura/no-floating-point-money -- variance computation for breach detection (informational); estimateRosterCost is the canonical source of truth and is round2'd
    const variance = input.estimatedTotalWages - input.periodBudget;
    // eslint-disable-next-line statura/no-floating-point-money -- percent calc for INFO-severity threshold check
    const percentOver = input.periodBudget > 0 ? (variance / input.periodBudget) * 100 : 0;
    if (percentOver > 5) {
      out.push({
        ruleCode: 'BUDGET_BREACH_ESTIMATE',
        severity: 'INFO',
        scope: 'PERIOD',
        scopeRefType: 'period',
        scopeRef: input.periodMeta.rosterPeriodId,
        message: `Estimated wages $${input.estimatedTotalWages.toFixed(2)} exceeds budget $${input.periodBudget.toFixed(2)} by ${percentOver.toFixed(1)}%.`,
        suggestedAction:
          'Review the cost panel; reduce hours / reassign to lower-cost classifications, or accept the variance with a budget revision note.',
      });
    }
  }

  // CARE_MINUTES_SHORTFALL — Phase 4 §4.8. Residential facilities must meet
  // their assessed, casemix-adjusted care-minute targets. The caller only
  // passes a breach when it has graded against a real care_minute_targets
  // row — never invent sector averages here (or in the message).
  if (input.careMinutesBreachDays != null && input.careMinutesBreachDays > 0) {
    const totalShortfall = input.careMinutesPeriodTotalShortfall ?? 0;
    const rnShortfall = input.careMinutesPeriodRnShortfall ?? 0;
    out.push({
      ruleCode: 'CARE_MINUTES_SHORTFALL',
      severity: 'WARN',
      scope: 'PERIOD',
      scopeRefType: 'period',
      scopeRef: input.periodMeta.rosterPeriodId,
      message: `Care minutes shortfall on ${input.careMinutesBreachDays} day(s): ${totalShortfall.toFixed(0)} total min + ${rnShortfall.toFixed(0)} RN min below the facility's assessed care-minute target.`,
      suggestedAction:
        'Add care-direct shifts (RN preferred for the RN component) or document deviation with regulatory justification.',
    });
  }

  if (!input.publishUnblockEnabled) return out;
  return out.map((gate) => {
    const shift = input.shifts.find(
      (s) => gateScopeRefType(s) === gate.scopeRefType && gateScopeRef(s) === gate.scopeRef,
    );
    const role = shift?.tenantPayRoleId ? tenantPayRoleById.get(shift.tenantPayRoleId) : undefined;
    return applyRosterPublishUnblock(
      gate,
      shift
        ? {
            ...shift,
            rosterRequirementKind: shift.rosterRequirementKind ?? role?.rosterRequirementKind,
            minimumScopeRole: shift.minimumScopeRole ?? role?.minimumScopeRole,
          }
        : undefined,
    );
  });
}

/** Independent row readiness; vacant alternatives must not conflict with each other.
 * Bulk confirmation still calls detectRosterGates on the full selected cohort. */
export function detectIndividualRosterGatePreviews(
  input: GateInput,
  candidateSlotIds: string[],
): Record<string, RosterGate[]> {
  const candidates = new Set(candidateSlotIds);
  return Object.fromEntries(
    candidateSlotIds.map((slotId) => [
      slotId,
      detectRosterGates({
        ...input,
        shifts: input.shifts.filter(
          (shift) => !candidates.has(gateScopeRef(shift)) || gateScopeRef(shift) === slotId,
        ),
      }),
    ]),
  );
}
